import { useContext, createContext } from 'react';
import {
  types,
  flow,
  Instance,
  getEnv,
  protect,
  unprotect,
} from 'mobx-state-tree';
// eslint-disable-next-line import/no-extraneous-dependencies
import makeInspectable from 'mobx-devtools-mst';
import { set } from 'mobx';
import { interpret } from 'xstate';
import pick from 'lodash/pick';
import { ValidationError } from 'yup';
import {
  Stripe,
  StripeElements,
  PaymentIntentResult,
  SetupIntentResult,
} from '@stripe/stripe-js';
import UtilityService from '../../../services/UtilityService';
import StripeService from '../../../services/StripeService';
import ConfigurationService from '../../../services/ConfigurationService';
import WindowService from '../../../services/WindowService';
import AmazonPayService from '../../../services/AmazonPayService';
import CartService from '../../../services/CartService';
import TokenizationService from '../../../services/TokenizationService';
import CustomerService from '../../../services/CustomerService';
import OrderService from '../../../services/OrderService';
import PayPalService, {
  PayPalNotAvailableServiceError,
  PayPalTransactionNotFoundServiceError,
} from '../../../services/PayPalService';
import { Configuration } from '../Configuration';
import { CartModel, PaymentProfileUpdateConfig } from '../Cart';
import { RootServices } from '../../../types/RootStore';
import { CustomerModel } from '../Customer';
import checkoutMachine, {
  CheckoutContext,
  CheckoutEvent,
  CheckoutInterpreter,
  CheckoutState,
  StripeWalletContext,
} from '../../machines/checkout-machine';
import { IStep } from '../../../types/Step';
import { CustomerLoginFormData } from '../../../components/input/CustomerLoginForm';
import { AddressContract, AddressField } from '../../../types/Address';
import PayPalPaymentRequest from '../../classes/PayPalPaymentRequest';
import IsoCodeService from '../../../services/IsoCodeService';
import ApiValidationError from '../../classes/ApiValidationError';
import ServiceError from '../../classes/ServiceError';
import {
  PayPalAcpHostedFieldsResponseData,
  PayPalAcpHostedFieldsSubmitResponse,
  PayPalAcpSubmitFunction,
  PayPalInfoContract,
  PayPalOnApproveData,
} from '../../../types/PayPal';
import { addressSchema } from '../../schemas/Address';
import AddressVerificationService from '../../../services/AddressVerificationService';
import AddressValidation from '../AddressValidation';
import AddressAutocompleteService from '../../../services/AddressAutocompleteService';
import { CartPaymentProfileCardContract } from '../../../types/PaymentProfile';
import { PayPalAcp3dsAction } from '../../../services/PayPal3ds';
import GiftCardService from '../../../services/GiftCardService';
import SpreedlyService from '../../../services/SpreedlyService';
import {
  SpreedlyValues,
  Endpoints,
  SiteLimitationKeys,
  APIValues,
} from '../../../utils/constants';
import {
  SpreedlyEventHandlers,
  SpreedlyRequiredCardInfoContract,
} from '../../../types/Spreedly';
import { CustomerPaymentProfileCardContract } from '../../../types/Customer';
import { ItemOptionContract } from '../../../types/Item';
import { applyInitialStyles } from '../../../utils/customStyles';
import {
  CartUpdateFromIntent,
  StripeInfoContract,
} from '../../../types/Stripe';
import { getLastShippingMethods } from '../../../components/payment/StripePaymentRequestButton/logic';

let rootStore: RootStoreInstance;

let storeUrl = UtilityService.getBaseUrl(window.location.origin);

if (storeUrl === 'http://localhost:3000') {
  storeUrl = 'http://www.v65.com'; // support local development when document.referrer refreshes with live reload and changes to self
}

const services: RootServices = {
  windowService: WindowService,
  addressAutocompleteService: new AddressAutocompleteService({
    apiUrl: `${storeUrl}${Endpoints.ADDRESS_VALIDATION}`,
  }),
  addressVerificationService: new AddressVerificationService({
    apiUrl: `${storeUrl}${Endpoints.ADDRESS_VALIDATION}`,
  }),
  amazonPayService: new AmazonPayService({ apiUrl: storeUrl }),
  configService: new ConfigurationService({ apiUrl: storeUrl }),
  cartService: new CartService({ apiUrl: storeUrl }),
  customerService: new CustomerService({ apiUrl: storeUrl }),
  giftCardService: new GiftCardService({ apiUrl: storeUrl }),
  isoCodeService: new IsoCodeService({ apiUrl: storeUrl }),
  orderService: new OrderService({ apiUrl: storeUrl }),
  payPalService: new PayPalService({ apiUrl: storeUrl }),
  spreedlyService: new SpreedlyService(),
  tokenizationService: new TokenizationService(),
  stripeService: new StripeService({ apiUrl: storeUrl }),
};

type Breadcrumbs = {
  steps: IStep[];
  currentStep: string;
};

const defaultBreadcrumbs: IStep[] = [
  {
    name: 'Cart',
    displayText: 'Cart',
    onClick: (): void => {
      services.windowService.navigate('/shoppingcart.asp');
    },
  },
  {
    name: 'Shipping',
    displayText: 'Shipping',
  },
  {
    name: 'Payment',
    displayText: 'Payment',
  },
];

const fonts: string[] = [
  'Arial, sans-serif',
  'Verdana, sans-serif',
  'Tahoma , sans-serif',
  'Trebuchet MS , sans-serif',
  'Times New Roman , serif',
  'Georgia  , serif',
  'Garamond  , serif',
  'Courier New , monospace',
  'Lato, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial',
];

export const RootModel = types
  .model({
    configuration: types.optional(Configuration, {}),
    cart: types.optional(CartModel, {}),
    customer: types.optional(CustomerModel, {}),
    validation: types.optional(AddressValidation, {}),
  })
  .volatile(() => ({
    storeUrl,
    isAmazonPayLoaded: false,
    machine: undefined as undefined | CheckoutInterpreter,
    breadcrumbs: {
      steps: defaultBreadcrumbs,
      currentStep: 'Shipping',
    } as Breadcrumbs,
    machineState: undefined as undefined | CheckoutState,
    isEmailRegistered: false,
    payPalAcpOnSubmit: undefined as undefined | PayPalAcpSubmitFunction,
    stripeServiceInitialized: false,
  }))
  .views(self => ({
    get currentStep(): string {
      const value = self.machineState?.value || 'Start';
      return typeof value === 'string'
        ? value
        : JSON.stringify(value).replace(/["{}]/gm, '').replace(/:/gm, '.');
    },
    stateMatches(stateToMatch: string): boolean {
      return self.machine?.state.matches(stateToMatch) ?? false;
    },
  }))
  .views(self => {
    const { windowService } = getEnv<RootServices>(self);
    const views = {
      get machineContext(): CheckoutContext | undefined {
        return self.machine?.state.context;
      },
      get stripeWalletParams(): StripeWalletContext | null {
        const queryStringParams = windowService.getQueryStringMap();
        const tokenProvider =
          queryStringParams.get('tokenProvider') ??
          queryStringParams.get('tokenprovider');
        if (tokenProvider && tokenProvider.toUpperCase() === 'STRIPE') {
          const token = queryStringParams.get('token') ?? null;
          const type =
            queryStringParams.get('walletType') ??
            queryStringParams.get('wallettype');
          if (typeof token === 'string') {
            return {
              token,
              type,
            };
          }
        }
        return null;
      },
      get payPalParams(): PayPalOnApproveData | null {
        const queryStringParams = windowService.getQueryStringMap();
        const paypalPaymentId =
          queryStringParams.get('paymentId') ??
          queryStringParams.get('paymentid');
        const paypalToken =
          queryStringParams.get('Token') ?? queryStringParams.get('token');
        const paypalPayerId =
          queryStringParams.get('PayerID') ?? queryStringParams.get('payerid');
        if (self.configuration.features.enablePayPalPpcp) {
          if (paypalToken && paypalPayerId) {
            return {
              payerID: paypalPayerId.toUpperCase(),
              orderID: paypalToken.toUpperCase(),
              paymentID: '',
            };
          }
        } else if (paypalPaymentId && paypalPayerId) {
          return {
            payerID: paypalPayerId.toUpperCase(),
            paymentID: paypalPaymentId.toUpperCase(),
            orderID: '',
          };
        }
        return null;
      },
      get isVenmoFromStorefront(): boolean {
        const queryStringParams = windowService.getQueryStringMap();
        const isVenmoParam = (queryStringParams.get('isVenmo') ??
          queryStringParams.get('isvenmo')) as string | null;
        return isVenmoParam?.toLowerCase() === 'true';
      },
      get amazonPayAccessToken(): string | null {
        const queryStringParams = windowService.getQueryStringMap();
        const amazonPayAccessToken = queryStringParams.get(
          'access_token'
        ) as string;

        return amazonPayAccessToken ?? null;
      },
      get invalidCountryShippingAddress(): boolean {
        const { shippingAddressCountry } = self.cart;
        if (!shippingAddressCountry) {
          return true;
        }
        return !self.configuration.availableCountries.includes(
          shippingAddressCountry
        );
      },
      get invalidStateShippingAddress(): boolean {
        const { shippingAddressState, shippingAddressCountry } = self.cart;
        if (!shippingAddressState || !shippingAddressCountry) {
          return true;
        }

        const currentStatesCodes = self.configuration.getCurrentStatesCodes(
          shippingAddressCountry as string
        );

        return currentStatesCodes.length
          ? !currentStatesCodes.includes(shippingAddressState)
          : false;
      },
      get showTaxAndShipping(): boolean {
        // TODO Update PayPal state here if it does not match actual
        return (
          self.cart.hasShippingMethodSelected &&
          Boolean(self.currentStep) &&
          (self.stateMatches('Guest.Payment') ||
            self.stateMatches('OneClick') ||
            self.stateMatches('AmazonPay') ||
            self.stateMatches('PayPal'))
        );
      },
    };
    return views;
  })
  .actions(self => {
    const {
      amazonPayService,
      customerService,
      isoCodeService,
      payPalService,
      spreedlyService,
      windowService,
      stripeService,
    } = getEnv<RootServices>(self);

    function registerPayPalAcpOnSubmit(
      onSubmit?: PayPalAcpSubmitFunction
    ): void {
      if (onSubmit) {
        set(self, { payPalAcpOnSubmit: onSubmit });
      }
    }

    function updateState(state: CheckoutState): void {
      /// MST was complaining that the state can be updated only through an action,
      /// and since this was being called from a StateListener function, I had to mark
      /// the root store as unprotected to set the value, and then protect it again.
      unprotect(self);
      set(self, { machineState: state });
      if (self.currentStep.includes('Guest.Payment')) {
        set(self, {
          breadcrumbs: { ...self.breadcrumbs, currentStep: 'Payment' },
        });
      } else if (self.stateMatches('Guest.Shipping')) {
        set(self, {
          breadcrumbs: { ...self.breadcrumbs, currentStep: 'Shipping' },
        });
      }
      protect(self);
    }

    function send(event: CheckoutEvent): void {
      if (event.type === 'SHIPPING_METHOD_SELECTED') {
        self.machine?.send({
          ...event,
          shippingFieldsCompleted: Boolean(
            self.cart.hasShippingMethodSelected &&
              self.cart.hasCustomerInfo &&
              self.cart.hasShippingAddress
          ),
        });
      } else {
        self.machine?.send(event);
      }
    }

    const updateStripeIntentFlow = flow(function* (): Generator<
      Promise<PaymentIntentResult | SetupIntentResult>,
      PaymentIntentResult | SetupIntentResult,
      PaymentIntentResult | SetupIntentResult
    > {
      return yield stripeService.updateIntent(
        self.configuration.payments.useSetupIntent
      );
    });

    const handleStripeNextAction = flow(function* (
      clientSecret: string | null
    ) {
      return yield stripeService.handleNextAction(clientSecret);
    });

    const postStripeIntentFlow = flow(function* () {
      if (
        self.configuration.isEnabled(
          SiteLimitationKeys.STRIPE_SERVICE_ENABLED
        ) &&
        self.configuration.hasStripePaymentMethodEnabled &&
        !self.cart.stripeInfo.isWallet
      ) {
        yield stripeService.postIntent(
          self.cart.shippingAddress,
          self.cart.updateStripeInfo
        );
      }
    });

    const putStripeIntentFlow = flow(function* () {
      if (
        self.configuration.isEnabled(
          SiteLimitationKeys.STRIPE_SERVICE_ENABLED
        ) &&
        self.configuration.hasStripePaymentMethodEnabled &&
        !self.configuration.payments.useSetupIntent &&
        self.stripeServiceInitialized &&
        self.cart.cartData.stripeInfo?.paymentIntentId
      ) {
        yield stripeService.putIntent(
          self.cart.shippingAddress,
          self.cart.stripeInfoData,
          self.cart.updateStripeInfo
        );
      }
    });

    const updateCartQuantityFlow = flow(function* (
      itemId: number,
      quantity: number
    ) {
      yield self.cart.updateQuantityFlow(itemId, quantity);

      yield putStripeIntentFlow();

      self.cart.resetLoadingVolatiles();
    });

    const removeCartItemFlow = flow(function* (
      cartItemId: number,
      itemOptions: ItemOptionContract[]
    ) {
      yield self.cart.removeItemFlow(cartItemId, itemOptions);

      if (self.cart.hasItems) {
        yield putStripeIntentFlow();
      }

      self.cart.resetLoadingVolatiles();
    });

    const updateCartShippingAddressFlow = flow(function* (
      isShippingAddressValid = true
    ) {
      self.cart.setFetchingShippingMethods(true);
      yield self.cart.updateCartFlow();
      self.cart.setFetchingShippingMethods(false);

      if (isShippingAddressValid) {
        send({ type: 'CART_UPDATED' });
      } else {
        send({ type: 'CART_UPDATED_WITH_MISSING_FIELDS' });
      }
    });

    const setShippingAddressOnCart = flow(function* (
      shippingAddress: AddressContract,
      setBillingSameAsShipping = false
    ) {
      let shippingAddressValid = false;
      self.cart.setMissingRequiredFields([]);
      try {
        yield addressSchema.validate(shippingAddress, { abortEarly: false });
        shippingAddressValid = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        // Capture the missing required fields
        const requiredFieldsMissing = (error as ValidationError).inner
          .filter(
            (validationError: ValidationError) =>
              validationError.path &&
              ['required', 'typeError'].includes(validationError.type)
          )
          .map((field: ValidationError) => field.path);
        self.cart.setMissingRequiredFields(
          requiredFieldsMissing as AddressField[]
        );
        shippingAddressValid = false;
      }
      self.cart.updateShippingAddress(shippingAddress);
      if (
        self.configuration.features.requireBillToSameAsShipTo ||
        setBillingSameAsShipping
      ) {
        self.cart.updateBillingAddress(shippingAddress);
      }
      if (self.customer.isLoading === false) {
        yield updateCartShippingAddressFlow(shippingAddressValid);
      }
    });

    const updateAuthenticatedCustomerShippingAddress = flow(function* (
      shippingAddress: AddressContract
    ) {
      // update the customer's shipping address
      const shippingAddressId = self.cart.shippingAddress.id;

      if (shippingAddressId) {
        yield self.customer.updateShippingAddressForAuthenticatedCustomer(
          shippingAddressId,
          shippingAddress
        );
      } else {
        yield self.customer.addShippingAddressToAuthenticatedCustomer(
          shippingAddress
        );
      }

      // if there is no billing address, add the shipping address as it
      let setBillingSameAsShipping = false;
      const billingAddressId = self.cart.billingAddress.id;
      if (!billingAddressId) {
        yield self.customer.addBillingAddressToAuthenticatedCustomer(
          shippingAddress
        );
        setBillingSameAsShipping = true;
      }
      yield setShippingAddressOnCart(shippingAddress, setBillingSameAsShipping);
    });

    const setCartCustomerValuesFromCustomer = (): void => {
      const existingAddressNumber =
        self.cart.billingAddress.phoneNumber ??
        self.cart.shippingAddress.phoneNumber;
      const existingNumber =
        self.cart.customer.phoneNumber ?? existingAddressNumber;
      self.cart.updateCustomer({
        emailAddress: self.customer.email ?? undefined,
        phoneNumber:
          existingNumber ?? self.customer.preferredBillingAddress?.phoneNumber,
        emailOptIn: self.customer.emailOptIn,
      });
      if (self.customer.id !== null) {
        self.cart.customer.updateId(self.customer.id);
      }
    };

    const mutateCardConfigForSpreedlySavedCard = (
      card: CustomerPaymentProfileCardContract
    ): CartPaymentProfileCardContract => {
      if (!self.configuration.features.enableSpreedly) {
        return card;
      }

      return {
        ...card,
        spreedlyPaymentToken:
          card.cardTokenProvider?.toUpperCase() ===
          SpreedlyValues.CARD_TOKEN_PROVIDER
            ? card.cardToken
            : null,
        paymentMethodFingerprint:
          card.cardTokenProvider?.toUpperCase() ===
          SpreedlyValues.CARD_TOKEN_PROVIDER
            ? card.paymentMethodFingerprint
            : null,
      };
    };

    const generateStripeConfigFromCardConfig = (
      card: CustomerPaymentProfileCardContract
    ): StripeInfoContract | undefined => {
      if (card.cardTokenProvider !== 'STRIPE') {
        return undefined;
      }

      return {
        cardBrand: card.cardBrand ?? null,
        clientSecretKey: null,
        last4: card.last4,
        expMonth: card.expMonth,
        expYear: card.expYear,
        paymentIntentId: null,
        setupIntentId: null,
        isSavedCard: true,
        paymentMethodId: card.cardToken,
      };
    };

    const setCartValuesFromCustomer = flow(function* () {
      setCartCustomerValuesFromCustomer();
      if (
        !self.cart.cartData.paymentProfile.method &&
        self.customer.preferredPaymentProfile
      ) {
        const savedProfile = { ...self.customer.preferredPaymentProfile };
        const methodId = parseInt(savedProfile.card.cardType.toString(), 10);
        self.customer.setSelectedPaymentProfileId(savedProfile.id ?? -1);
        const configs: PaymentProfileUpdateConfig = {
          cardConfig: mutateCardConfigForSpreedlySavedCard(savedProfile.card),
          stripeConfig: generateStripeConfigFromCardConfig(savedProfile.card),
        };

        if (savedProfile.card.cardTokenProvider === 'STRIPE') {
          configs.stripeConfig = {
            ...self.cart.stripeInfo,
            ...configs.stripeConfig,
          };
        }
        self.cart.updatePaymentProfile(
          {
            id: methodId,
            // This seems wrong, but it is a V1 quirk in translating between
            // a saved 'paymentProfile' and an enabled 'paymentMethod'
            method: savedProfile.card.cardTypeName,
            methodType: savedProfile.method,
          },
          configs
        );
        self.cart.setNeedsNewPciToken(true);
      }
      set(self, { isEmailRegistered: true });
      if (!self.cart.hasBillingAddress) {
        let cartBillingAddress = self.customer.preferredBillingAddress;
        if (self.configuration.features.requireBillToSameAsShipTo) {
          cartBillingAddress = self.customer.preferredShippingAddress;
        }
        self.cart.updateBillingAddress(cartBillingAddress as AddressContract);
      }
      if (!self.cart.hasShippingAddress) {
        yield setShippingAddressOnCart(
          self.customer.preferredShippingAddress as AddressContract
        );
      }
      yield postStripeIntentFlow();
      if (self.stateMatches('OneClick.Loading')) {
        send({ type: 'CART_UPDATED' });
      }
    });

    const setEmailRegistered = flow(function* (emailAddress: string) {
      const result = yield customerService.isEmailRegisteredAsync(emailAddress);
      set(self, { isEmailRegistered: result });
    });

    const loginFlow = flow(function* (formData: CustomerLoginFormData) {
      yield self.customer.loginFlow(formData);
    });

    const loadCustomerFlow = flow(function* () {
      self.customer.setIsLoading(true);
      yield self.customer.getCustomerFlow();
      if (self.cart.shippingAddress.hasMinimalAddress) {
        const newShipAddress =
          yield self.customer.addShippingAddressToAuthenticatedCustomer(
            self.cart.shippingAddress
          );
        self.cart.updateShippingAddress(newShipAddress);
      }
      if (self.cart.billingAddress.hasMinimalAddress) {
        const newBillAddress =
          yield self.customer.addBillingAddressToAuthenticatedCustomer(
            self.cart.billingAddress
          );
        self.cart.updateBillingAddress(newBillAddress);
      }
      if (!self.cart.isPopulated) {
        yield self.cart.getCartFlow();
      }
      set(self, { isEmailRegistered: true });
      self.customer.setIsLoading(false);
    });

    const startNewCheckout = flow(function* () {
      yield self.customer.logoutFlow();
      windowService.reload();
    });

    const setAmazonLoaded = (isLoaded = false): void => {
      set(self, { isAmazonPayLoaded: isLoaded });
    };

    const initializeCheckout = async (): Promise<void> => {
      await self.cart.getCartFlow();
      if (!self.configuration.features.disableCustomerLogin) {
        await self.customer.getCustomerFlow();
      }
      self.configuration.getEnabledPaymentMethodsFlow();
      self.validation.setIsEnabled(
        self.configuration.features.enableAddressValidation
      );
      self.cart.setIsUsingPayPalAcp(
        Boolean(self.configuration.paypalExpress.acpClientToken)
      );
      payPalService.useV2Endpoints =
        self.configuration.features.enablePayPalPpcp;
      self.cart.paypalInfo.setIsVenmo(self.isVenmoFromStorefront);
      send({
        type: 'CONFIG_FETCHED',
        customer: self.customer.customerData,
        paypalParams: self.payPalParams,
        amazonPayToken: self.amazonPayAccessToken,
        stripeWallet: self.configuration.payments.stripeWalletEnabled
          ? self.stripeWalletParams
          : null,
      });
    };

    const initializeAmazonCheckout = (orderReferenceId: string): void => {
      const amazonMethod = AmazonPayService.findAmazonPayPaymentMethod(
        self.configuration.enabledPaymentMethods
      );
      if (!amazonMethod) {
        throw new ServiceError(
          'AmazonPayService',
          'Amazon is no longer enabled for payment'
        );
      }
      if (self.customer.isLoggedIn) {
        setCartCustomerValuesFromCustomer();
      }
      self.cart.updatePaymentProfile(amazonMethod, {
        amazonConfig: {
          // TODO: Fix camelcase
          // eslint-disable-next-line
          access_token: self.machineContext?.amazonPayToken as string,
          orderReferenceId,
        },
      });
    };

    const updateAddressFromAmazon = async (): Promise<void> => {
      const { access_token: accessToken, orderReferenceId } =
        self.cart.amazonInfo;
      if (!accessToken || !orderReferenceId) {
        throw new ServiceError(
          'AmazonPayService',
          'Unable to recover transaction details'
        );
      }
      const amazonAddress = await amazonPayService.getOrderDetails(
        orderReferenceId,
        accessToken
      );
      if (amazonAddress.emailAddress && !self.customer.isLoggedIn) {
        self.cart.updateCustomer({
          emailAddress: amazonAddress.emailAddress,
          phoneNumber: amazonAddress.phoneNumber ?? undefined,
        });
      }
      const fieldsToUse = [
        'firstName',
        'lastName',
        'address1',
        'address2',
        'city',
        'state',
        'postalCode',
        'country',
        'phoneNumber',
      ];
      const trimmedAddress = pick(
        amazonAddress,
        fieldsToUse
      ) as AddressContract;
      self.cart.updateShippingAddress({
        ...trimmedAddress,
        preferred: true,
        id: null,
        residential: true,
        companyName: null,
      });
      self.cart.updateBillingAddress({
        ...trimmedAddress,
        preferred: true,
        id: null,
        companyName: null,
      });
    };

    const updatePayPalAddressFlow = flow(function* (
      addressUpdate: AddressContract
    ) {
      self.cart.setMissingRequiredFields([]);
      self.cart.updateShippingAddress(addressUpdate);
      self.cart.updateBillingAddress(addressUpdate);
      try {
        yield addressSchema.validate(addressUpdate, { abortEarly: false });
        self.cart.updateCustomer({
          phoneNumber: addressUpdate.phoneNumber ?? undefined,
        });
        self.cart.setFetchingShippingMethods(true);
        yield self.cart.updateCartFlow();
        self.cart.setFetchingShippingMethods(false);
        send({ type: 'PAYPAL_DATA_LOADED' });
      } catch (error) {
        // Capture the missing required fields
        const requiredFieldsMissing = (error as ValidationError).inner
          .filter((validationError: ValidationError) =>
            ['required', 'typeError'].includes(validationError.type)
          )
          .map((field: ValidationError) => field.path);
        self.cart.setMissingRequiredFields(
          requiredFieldsMissing as AddressField[]
        );
        send({ type: 'PAYPAL_DATA_LOADED_WITH_MISSING_FIELDS' });
      }
    });

    const initializePayPalCheckout = flow(function* (
      payerId: string,
      orderId: string
    ) {
      const payPalMethod = payPalService.findPayPalPaymentMethod(
        self.configuration.enabledPaymentMethods
      );
      if (!payPalMethod) {
        throw PayPalNotAvailableServiceError;
      }
      const paymentDetails = yield payPalService.getPaymentDetails(orderId);

      if (!paymentDetails) {
        throw PayPalTransactionNotFoundServiceError;
      }

      if (self.customer.isLoggedIn) {
        setCartCustomerValuesFromCustomer();
      } else if (
        !self.cart.customer.emailAddress ||
        self.cart.customer.emailAddress === ''
      ) {
        const customerUpdate =
          PayPalService.buildNewCustomerUpdate(paymentDetails);
        // For PC-253
        // In "Guest" flows we normally populate customer from cart when the order is placed.
        // self.customer.updateCustomer(customerUpdate);
        self.cart.updateCustomer({
          emailAddress: customerUpdate.email,
        });
        setEmailRegistered(customerUpdate.email);
      }
      const countryCode =
        paymentDetails.transactions.item_list?.shipping_address?.country_code;
      const countryName = countryCode
        ? yield isoCodeService.getNameFromIsoCode(countryCode)
        : null;
      const addressUpdate = PayPalService.buildAddressUpdate(
        paymentDetails,
        countryName,
        self.cart.cartData.shippingAddress
      );

      const paypalConfig = PayPalService.buildPayPalInfoUpdate(
        paymentDetails,
        payerId,
        payPalMethod.id
      );
      self.cart.updatePaymentProfile(payPalMethod, { paypalConfig });
      yield updatePayPalAddressFlow(addressUpdate);
      // For PC-253
      // Send event here or after this method resolves

      windowService.removeQueryString();
    });

    const createPayPalOrder = async (): Promise<string> => {
      const { cartData } = self.cart;
      const totals = PayPalService.buildPaymentTotalsContainer(cartData.totals);
      const { isDelayedCapture, defaultCurrency } =
        self.configuration.paypalExpress;

      const shippingAddress = self.cart.hasShippingAddress
        ? PayPalService.buildPaymentAddressContainer({
            ...cartData.shippingAddress,
            country: await isoCodeService.getIsoCodeFromName(
              cartData.shippingAddress.country ?? ''
            ),
          })
        : undefined;

      const request = new PayPalPaymentRequest(
        totals,
        isDelayedCapture,
        defaultCurrency,
        shippingAddress
      );
      const result = await payPalService.createPayment(request);

      if (result.errors.count) {
        const errors = PayPalService.extractCreatePaymentError(result);
        throw new ApiValidationError(errors[0].field, errors);
      }

      return result.token;
    };

    const setPayPalAcpDetails = (data: PayPalAcpHostedFieldsResponseData) => {
      const payPalMethod = PayPalService.findPayPalPaymentMethod(
        self.configuration.enabledPaymentMethods
      );
      if (!payPalMethod) {
        throw PayPalNotAvailableServiceError;
      }
      const cardConfig = {
        cardType: payPalMethod.id,
        cardTypeName: data.creditCardType,
        last4: data.creditCardLastFour,
      } as CartPaymentProfileCardContract;
      const paypalConfig: PayPalInfoContract = {
        totalAmount: self.cart.cartData.totals.grandTotal,
        token: data.orderId,
        paymentStatus: 'Pending',
        paymentMethodId: payPalMethod.id,
        transactionId: data.orderId,
        payerId: '',
        useStoreCredit: false,
      };

      self.cart.updatePaymentProfile(payPalMethod, {
        paypalConfig,
        cardConfig,
      });
    };

    const getPayPal3dsAction = (
      cardResponse: PayPalAcpHostedFieldsSubmitResponse
    ): PayPalAcp3dsAction => {
      return payPalService.calculate3dsAction(cardResponse);
    };

    const initializeSpreedlyInputs = (
      spreedlyEventHandlers: SpreedlyEventHandlers
    ): void => {
      const style = self.configuration.isEnabled(
        'Feature_Checkout_Customization_Enabled'
      )
        ? `${SpreedlyValues.FIELD_STYLE} color: ${self.configuration.styles.formAccentColor};`
        : SpreedlyValues.FIELD_STYLE;
      spreedlyService.initializeSpreedly();
      spreedlyService.renderSpreedly([
        {
          field: 'number',
          style,
          fieldType: 'number',
          numberFormat: 'prettyFormat',
        },
        {
          field: 'cvv',
          style,
          fieldType: 'number',
        },
      ]);
      spreedlyService.setEventHandlers(spreedlyEventHandlers);
      const { card } = self.cart.cartData.paymentProfile;
      if (
        spreedlyEventHandlers.onRecache &&
        card?.spreedlyPaymentToken &&
        card.cardTypeName &&
        card.last4
      ) {
        spreedlyService.setupRecache(card.spreedlyPaymentToken, {
          card_type: SpreedlyService.convertV1ToSpreedlyCardType(
            card.cardTypeName
          ).toLowerCase() as spreedly.SpreedlyCardType,
          last_four_digits: card.last4,
        });
      }
    };

    const validateSpreedlyInput = (): void => {
      spreedlyService.validateCard();
    };

    const tokenizeSpreedlyCard = (
      requiredCardInfo: SpreedlyRequiredCardInfoContract
    ): void => {
      spreedlyService.tokenizeCard(requiredCardInfo);
    };

    const recacheSpreedlyCard = (): void => {
      spreedlyService.recacheCard();
    };

    const setStripeElements = (
      stripe: Stripe,
      elements: StripeElements
    ): void => {
      stripeService.setElements(stripe, elements);
      set(self, { stripeServiceInitialized: true });
    };

    const applyInitialStyleFromQs = (): void => {
      const queryStringParams = windowService.getQueryStringMap();
      const text = queryStringParams.get('text') ?? null;
      const bg = queryStringParams.get('bg') ?? null;
      const font = queryStringParams.get('font') ?? null;

      const textMatch = text && /^[A-Fa-f0-9]{6}$/.test(text);
      const bgMatch = bg && /^[A-Fa-f0-9]{6}$/.test(bg);
      const fontMatch = font && fonts.includes(font);

      if (textMatch && bgMatch && fontMatch) {
        applyInitialStyles({ text, bg, font });
        WindowService.removeQueryString();
      }
    };

    const setCartForStripeWalletCheckout = (
      cartUpdate: CartUpdateFromIntent
    ): void => {
      const { customer, shipping, shippingMethodId, billing, payment } =
        cartUpdate;
      self.cart.updateCustomer(customer);
      self.cart.updateShippingAddress(shipping);
      self.cart.updateBillingAddress(billing);
      const shippingMethods = getLastShippingMethods();
      if (shippingMethods?.length) {
        self.cart.setShippingMethods(shippingMethods);
      }
      if (shippingMethodId) {
        self.cart.updateShippingMethod(parseInt(shippingMethodId, 10));
      }
      self.cart.setUseShippingForBilling(
        self.cart.isBillingAddressSameAsShipping
      );
      self.cart.updateStripeInfo({ ...payment, isWallet: true });
      const vPayMethod = self.configuration.enabledPaymentMethods.find(
        method => method.methodType === APIValues.STRIPE_METHOD
      );
      if (vPayMethod) {
        self.cart.updatePaymentProfile(vPayMethod, { stripeConfig: payment });
        self.cart.setPaymentMethodType(vPayMethod.methodType);
      }
    };

    const getIncomingWalletIntent =
      async (): Promise<CartUpdateFromIntent | null> => {
        if (self.configuration.payments.isVpayEnabled) {
          const walletToken = self.stripeWalletParams?.token;
          if (walletToken) {
            const getIntentResponse = await stripeService.getIntent(
              walletToken
            );
            WindowService.removeQueryString();
            return getIntentResponse;
          }
        }
        WindowService.removeQueryString();
        return null;
      };

    const sendWalletValidationEvent = () => {
      if (!self.cart.hasShippingAddress) {
        send({ type: 'WALLET_NEEDS_SHIPPING' });
      } else if (!self.cart.hasBillingAddress) {
        send({ type: 'WALLET_NEEDS_BILLING' });
      } else {
        send({ type: 'WALLET_IS_VALID' });
      }
    };

    const getStripeService = () => stripeService;

    return {
      getStripeService,
      afterCreate(): void {
        set(self, {
          machine:
            process.env.NODE_ENV === 'development'
              ? interpret(checkoutMachine, { devTools: true })
              : interpret(checkoutMachine),
        });
        self.machine?.onTransition(updateState);
        self.machine?.start();
      },
      applyInitialStyleFromQs,
      setStripeElements,
      loginFlow,
      loadCustomerFlow,
      initializeCheckout,
      initializeAmazonCheckout,
      initializePayPalCheckout,
      createPayPalOrder,
      setPayPalAcpDetails,
      getPayPal3dsAction,
      registerPayPalAcpOnSubmit,
      send,
      setAmazonLoaded,
      setCartValuesFromCustomer,
      setShippingAddressOnCart,
      updateCartShippingAddressFlow,
      updateAddressFromAmazon,
      setEmailRegistered,
      updatePayPalAddressFlow,
      startNewCheckout,
      initializeSpreedlyInputs,
      validateSpreedlyInput,
      tokenizeSpreedlyCard,
      recacheSpreedlyCard,
      postStripeIntentFlow,
      putStripeIntentFlow,
      updateStripeIntentFlow,
      updateCartQuantityFlow,
      removeCartItemFlow,
      handleStripeNextAction,
      setCartForStripeWalletCheckout,
      getIncomingWalletIntent,
      sendWalletValidationEvent,
      updateAuthenticatedCustomerShippingAddress,
    };
  });

export interface RootStoreInstance extends Instance<typeof RootModel> {}

export function getRootStore(): RootStoreInstance {
  if (rootStore) return rootStore;

  rootStore =
    process.env.NODE_ENV === 'development'
      ? (makeInspectable(RootModel.create({}, services)) as RootStoreInstance)
      : RootModel.create({}, services);

  return rootStore;
}

const RootStoreContext = createContext<RootStoreInstance | null>(null);

export const { Provider } = RootStoreContext;
export function useMst(): RootStoreInstance {
  const store = useContext<RootStoreInstance | null>(RootStoreContext);
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider');
  }
  return store;
}
